import { useEffect, useRef, useState } from "react";
import { useQuery } from "react-query";
import Supercluster from "supercluster";
import { apiV2 } from "../../../api/axiosConfig";
import { useMapSettings } from "../../operational-map/context/useMapSettings";

// --- Types ---

export type TripItem = {
    tripID: string;
    userID: string;
    clientID: string;
    type: string;
    subType: string;
    startTime: string;
    endTime: string;
    formatted: string;
    coordinates: number[];
};

export type TripItemPopupItem = {
    tripID: string;
    userID: string;
    type: string;
    subType: string;
    startTime: string;
    endTime: string;
    formatted: string;
};

export type TripItemPopupState = {
    visible: boolean;
    position: { x: number; y: number };
    items: TripItemPopupItem[];
    // Key of the itinerary item that was clicked (tripID:type:subType:startTime)
    selectedItemKey: string;
};

// --- Trip detail types (from GET v2/ops/trips/:tripID) ---

export type TripDetailLocation = {
    formatted: string;
    countryISO: string;
    countryName: string;
    town: string;
    timezone: string;
    lat?: number;
    lng?: number;
};

export type TripDetailItineraryItem = {
    type: string;
    subType: string;
    startTime: string;
    endTime: string;
    locations: TripDetailLocation[];
    details?: Record<string, string>;
};

export type TripDetailUser = {
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
};

export type TripDetail = {
    id: string;
    userID: string;
    clientID: string;
    clientName: string;
    startTime: string;
    endTime: string;
    itinerary: TripDetailItineraryItem[];
    user: TripDetailUser;
};

export async function fetchTripDetail(tripID: string): Promise<TripDetail> {
    const res = await apiV2.get(`ops/trips/${tripID}`);
    return res.data as TripDetail;
}

// --- WebGL helpers ---

function compileShader(gl: WebGLRenderingContext, type: number, src: string): WebGLShader {
    const shader = gl.createShader(type)!;
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
        throw new Error(gl.getShaderInfoLog(shader) ?? "Shader compile error");
    }
    return shader;
}

function buildProgram(gl: WebGLRenderingContext, vert: string, frag: string): WebGLProgram {
    const vs = compileShader(gl, gl.VERTEX_SHADER, vert);
    const fs = compileShader(gl, gl.FRAGMENT_SHADER, frag);
    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    return prog;
}

function loadTexture(gl: WebGLRenderingContext, src: string, onLoad?: () => void): WebGLTexture {
    const tex = gl.createTexture()!;
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array([0, 0, 0, 0]));
    const img = new Image();
    img.onload = () => {
        gl.bindTexture(gl.TEXTURE_2D, tex);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 1);
        gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img);
        gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, 0);
        gl.generateMipmap(gl.TEXTURE_2D);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
        gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
        const ext = gl.getExtension("EXT_texture_filter_anisotropic")
                 ?? gl.getExtension("MOZ_EXT_texture_filter_anisotropic")
                 ?? gl.getExtension("WEBKIT_EXT_texture_filter_anisotropic");
        if (ext) gl.texParameterf(gl.TEXTURE_2D, ext.TEXTURE_MAX_ANISOTROPY_EXT, 4);
        onLoad?.();
    };
    img.src = src;
    return tex;
}

// --- Shaders ---

const ICON_VERT = `
    attribute vec2 a_position;
    attribute float a_highlighted;
    void main() {
        gl_Position = vec4(a_position, 0.0, 1.0);
        gl_PointSize = a_highlighted > 0.5 ? 32.0 : 24.0;
    }
`;

const ICON_FRAG = `
    #extension GL_EXT_shader_texture_lod : enable
    precision mediump float;
    uniform sampler2D u_texture;
    void main() {
        vec2  uv   = gl_PointCoord - 0.5;
        float dist = length(uv);

        float sm        = 0.04;
        float fillR     = 0.5;
        float iconScale = 0.71;

        if (dist > fillR) discard;

        // Blue filled circle (#007dd2)
        float fillAlpha = smoothstep(fillR, fillR - sm, dist);
        vec4 col = vec4(0.0, 0.49, 0.824, fillAlpha);

        vec2 iconUV = (gl_PointCoord - 0.5) / iconScale + 0.5;
        if (iconUV.x >= 0.0 && iconUV.x <= 1.0 && iconUV.y >= 0.0 && iconUV.y <= 1.0) {
            #ifdef GL_EXT_shader_texture_lod
                vec4 iconColor = texture2DLodEXT(u_texture, iconUV, 0.0);
            #else
                vec4 iconColor = texture2D(u_texture, iconUV, -1.0);
            #endif
            col.rgb = iconColor.rgb + col.rgb * (1.0 - iconColor.a);
        }

        if (col.a < 0.01) discard;
        gl_FragColor = vec4(col.rgb * col.a, col.a);
    }
`;

// --- Icon paths per itinerary subtype ---

const DEFAULT_ICON = "/assets/mapicons/unknown_transparent.png";
const ICON_BY_SUBTYPE: Record<string, string> = {
    flight:           "/assets/mapicons/flight_transparent.png",
    taxi:             "/assets/mapicons/taxi_transparent.png",
    ferry:            "/assets/mapicons/ferry_transparent.png",
    train:            "/assets/mapicons/train_transparent.png",
    rental:           "/assets/mapicons/car_rental_transparent.png",
    private_car:      "/assets/mapicons/car_transparent.png",
    bus:              "/assets/mapicons/bus_transparent.png",
    hotel:            "/assets/mapicons/hotel_transparent.png",
    private_property: "/assets/mapicons/house_rental_transparent.png",
    home:             "/assets/mapicons/house_transparent.png",
};

const CLICK_RADIUS = 30;
const CLUSTER_RADIUS = 50;
const CLUSTER_MAX_ZOOM = 14;

// --- Cluster colours ---
const CLUSTER_BG   = "#007dd2";
const CLUSTER_TEXT = "#ffffff";

// --- Rendered point for hit testing ---

type RenderedPoint = {
    x: number;
    y: number;
    item?: TripItem;
    isCluster: boolean;
    clusterID?: number;
    count?: number;
};

// --- Overlay factory ---

function createTripItemsOverlay(
    map: google.maps.Map,
    onRenderedPoints: (pts: RenderedPoint[]) => void,
    onRedraw: () => void,
) {
    // WebGL canvas for individual trip icons
    const glCanvas = document.createElement("canvas");
    glCanvas.style.position = "absolute";
    glCanvas.style.top = "0";
    glCanvas.style.left = "0";
    glCanvas.style.pointerEvents = "none";
    map.getDiv().appendChild(glCanvas);

    // 2D canvas for cluster bubbles (drawn on top)
    const clusterCanvas = document.createElement("canvas");
    clusterCanvas.style.position = "absolute";
    clusterCanvas.style.top = "0";
    clusterCanvas.style.left = "0";
    clusterCanvas.style.pointerEvents = "none";
    map.getDiv().appendChild(clusterCanvas);

    const gl = glCanvas.getContext("webgl", { alpha: true, premultipliedAlpha: true })!;
    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

    const prog = buildProgram(gl, ICON_VERT, ICON_FRAG);

    const textures: Record<string, WebGLTexture> = {};
    textures["default"] = loadTexture(gl, DEFAULT_ICON, onRedraw);
    const srcToTexture = new Map<string, WebGLTexture>();
    for (const [key, src] of Object.entries(ICON_BY_SUBTYPE)) {
        if (!srcToTexture.has(src)) srcToTexture.set(src, loadTexture(gl, src, onRedraw));
        textures[key] = srcToTexture.get(src)!;
    }

    let isReady = false;
    const overlay = new (class extends google.maps.OverlayView {
        onAdd()    { isReady = true; }
        draw()     {}
        onRemove() { isReady = false; }
        getProjectionSafe(): google.maps.MapCanvasProjection | null {
            if (!isReady) return null;
            try { return this.getProjection(); } catch { return null; }
        }
    })();
    overlay.setMap(map);

    type DrawPoint = { lng: number; lat: number; item?: TripItem; isCluster: boolean; clusterID?: number; count?: number };
    let drawPoints: DrawPoint[] = [];
    let hoveredIDs = new Set<string>(); // tripID:type:subType:startTime
    let rafId: number | null = null;

    function itemKey(item: TripItem) { return `${item.tripID}:${item.type}:${item.subType}:${item.startTime}`; }

    function resizeCanvases(width: number, height: number, dpr: number) {
        for (const c of [glCanvas, clusterCanvas]) {
            c.style.width  = `${width}px`;
            c.style.height = `${height}px`;
            c.width  = width  * dpr;
            c.height = height * dpr;
        }
    }

    function draw() {
        const projection = overlay.getProjectionSafe();
        if (!projection) return;

        const mapDiv = map.getDiv() as HTMLElement;
        const dpr    = window.devicePixelRatio || 1;
        const width  = mapDiv.offsetWidth;
        const height = mapDiv.offsetHeight;
        if (!width || !height) return;

        resizeCanvases(width, height, dpr);

        gl.viewport(0, 0, glCanvas.width, glCanvas.height);
        gl.clearColor(0, 0, 0, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);

        const ctx2d = clusterCanvas.getContext("2d")!;
        ctx2d.clearRect(0, 0, clusterCanvas.width, clusterCanvas.height);
        ctx2d.scale(dpr, dpr);

        const allPts: RenderedPoint[] = [];

        if (drawPoints.length === 0) {
            ctx2d.setTransform(1, 0, 0, 1, 0, 0);
            onRenderedPoints(allPts);
            return;
        }

        const individuals: DrawPoint[] = [];
        const clusters: DrawPoint[] = [];
        for (const dp of drawPoints) {
            if (dp.isCluster) clusters.push(dp);
            else individuals.push(dp);
        }

        // --- Draw individual icons via WebGL ---
        const byIcon: Record<string, { positions: number[]; highlighted: number[]; pts: RenderedPoint[] }> = {};

        for (const dp of individuals) {
            if (!isFinite(dp.lng) || !isFinite(dp.lat)) continue;
            const pixel = projection.fromLatLngToContainerPixel(new google.maps.LatLng(dp.lat, dp.lng));
            if (!pixel) continue;

            const iconKey = dp.item && (dp.item.subType in ICON_BY_SUBTYPE) ? dp.item.subType : "default";
            if (!byIcon[iconKey]) byIcon[iconKey] = { positions: [], highlighted: [], pts: [] };

            const nx = (pixel.x / width) * 2 - 1;
            const ny = 1 - (pixel.y / height) * 2;

            byIcon[iconKey].positions.push(nx, ny);
            byIcon[iconKey].highlighted.push(dp.item && hoveredIDs.has(itemKey(dp.item)) ? 1.0 : 0.0);
            byIcon[iconKey].pts.push({ x: pixel.x, y: pixel.y, item: dp.item, isCluster: false });
        }

        gl.useProgram(prog);
        const aPosLoc = gl.getAttribLocation(prog, "a_position");
        const aHlLoc  = gl.getAttribLocation(prog, "a_highlighted");

        for (const [iconKey, group] of Object.entries(byIcon)) {
            if (group.positions.length === 0) continue;

            const tex = textures[iconKey] ?? textures["default"];
            const count = group.positions.length / 2;

            const posBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, posBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(group.positions), gl.DYNAMIC_DRAW);
            gl.enableVertexAttribArray(aPosLoc);
            gl.vertexAttribPointer(aPosLoc, 2, gl.FLOAT, false, 0, 0);

            const hlBuf = gl.createBuffer()!;
            gl.bindBuffer(gl.ARRAY_BUFFER, hlBuf);
            gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(group.highlighted), gl.DYNAMIC_DRAW);
            gl.enableVertexAttribArray(aHlLoc);
            gl.vertexAttribPointer(aHlLoc, 1, gl.FLOAT, false, 0, 0);

            gl.activeTexture(gl.TEXTURE0);
            gl.bindTexture(gl.TEXTURE_2D, tex);
            gl.uniform1i(gl.getUniformLocation(prog, "u_texture"), 0);

            gl.drawArrays(gl.POINTS, 0, count);
            allPts.push(...group.pts);
        }

        // --- Draw cluster bubbles via 2D canvas ---
        for (const dp of clusters) {
            if (!isFinite(dp.lng) || !isFinite(dp.lat)) continue;
            const pixel = projection.fromLatLngToContainerPixel(new google.maps.LatLng(dp.lat, dp.lng));
            if (!pixel) continue;

            const hovered = dp.clusterID !== undefined && hoveredIDs.has(`cluster_${dp.clusterID}`);
            const r = hovered ? 17 : 12;
            ctx2d.beginPath();
            ctx2d.arc(pixel.x, pixel.y, r, 0, Math.PI * 2);
            ctx2d.fillStyle = CLUSTER_BG;
            ctx2d.fill();

            ctx2d.fillStyle = CLUSTER_TEXT;
            ctx2d.font = `bold ${hovered ? 13 : 10}px sans-serif`;
            ctx2d.textAlign = "center";
            ctx2d.textBaseline = "middle";
            ctx2d.fillText(String(dp.count ?? 0), pixel.x, pixel.y + 1);

            allPts.push({ x: pixel.x, y: pixel.y, isCluster: true, clusterID: dp.clusterID, count: dp.count });
        }

        ctx2d.setTransform(1, 0, 0, 1, 0, 0);
        onRenderedPoints(allPts);
    }

    function startLoop() {
        if (rafId !== null) return;
        const loop = () => {
            draw();
            rafId = drawPoints.length > 0 ? requestAnimationFrame(loop) : null;
        };
        rafId = requestAnimationFrame(loop);
    }

    const mapListeners = [
        google.maps.event.addListener(map, "idle",           draw),
        google.maps.event.addListener(map, "bounds_changed", draw),
    ];

    return {
        setDrawPoints(next: DrawPoint[]) {
            drawPoints = next;
            if (next.length > 0) startLoop();
            else draw();
        },
        setHoveredIDs(ids: Set<string>) {
            hoveredIDs = ids;
            draw();
        },
        destroy() {
            if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null; }
            mapListeners.forEach((l) => google.maps.event.removeListener(l));
            overlay.setMap(null);
            if (glCanvas.parentNode) glCanvas.parentNode.removeChild(glCanvas);
            if (clusterCanvas.parentNode) clusterCanvas.parentNode.removeChild(clusterCanvas);
        },
    };
}

type TripItemsOverlay = ReturnType<typeof createTripItemsOverlay>;

// --- Supercluster setup ---

type TripClusterProps = { item: TripItem };

function buildClusterIndex(items: TripItem[]): Supercluster<TripClusterProps> {
    const index = new Supercluster<TripClusterProps>({ radius: CLUSTER_RADIUS, maxZoom: CLUSTER_MAX_ZOOM });
    const points: GeoJSON.Feature<GeoJSON.Point, TripClusterProps>[] = items.map((item) => ({
        type: "Feature",
        properties: { item },
        geometry: { type: "Point", coordinates: [item.coordinates[0], item.coordinates[1]] },
    }));
    index.load(points);
    return index;
}

// --- Fetch ---

async function fetchTripItems(clientID: string | undefined, types: string[], bufferHours: number): Promise<TripItem[]> {
    const res = await apiV2.get("ops/trips", {
        params: {
            clientID: clientID ?? undefined,
            types: types.join(","),
            bufferHours: bufferHours > 0 ? bufferHours : undefined,
        },
    });
    return res.data as TripItem[];
}

// --- Hook ---

export type TripItemsLayerResult = {
    popup: TripItemPopupState;
    setPopup: (s: TripItemPopupState) => void;
    getHitsAtPoint: (clientX: number, clientY: number) => { items: TripItem[]; hoveredID: string | null };
    setHoveredIDs: (ids: Set<string>) => void;
    openPopupForItems: (items: TripItem[], position: { x: number; y: number }) => void;
    tripCount: number;
};

export function useTripItemsLayer(map: google.maps.Map | null): TripItemsLayerResult {
    const { settings } = useMapSettings();
    const { clientID, tripTypes, tripBufferHours } = settings;

    const overlayRef = useRef<TripItemsOverlay | null>(null);
    const renderedPointsRef = useRef<RenderedPoint[]>([]);
    const clusterIndexRef = useRef<Supercluster<TripClusterProps> | null>(null);

    const [popup, setPopup] = useState<TripItemPopupState>({ visible: false, position: { x: 0, y: 0 }, items: [], selectedItemKey: "" });

    const enabled = tripTypes.length > 0;

    const { data } = useQuery(
        ["trip-items-layer", clientID, tripTypes, tripBufferHours],
        () => fetchTripItems(clientID, tripTypes, tripBufferHours),
        { refetchInterval: 60_000, enabled }
    );

    useEffect(() => {
        if (!map) return;
        const overlay = createTripItemsOverlay(
            map,
            (pts) => { renderedPointsRef.current = pts; },
            () => { pushClusters(map, overlayRef.current, clusterIndexRef.current); }
        );
        overlayRef.current = overlay;

        const listeners = [
            google.maps.event.addListener(map, "idle",           () => pushClusters(map, overlayRef.current, clusterIndexRef.current)),
            google.maps.event.addListener(map, "bounds_changed", () => pushClusters(map, overlayRef.current, clusterIndexRef.current)),
        ];

        return () => {
            overlay.destroy();
            overlayRef.current = null;
            renderedPointsRef.current = [];
            listeners.forEach((l) => google.maps.event.removeListener(l));
        };
    }, [map]);

    useEffect(() => {
        const next = enabled ? (data ?? []) : [];
        clusterIndexRef.current = next.length > 0 ? buildClusterIndex(next) : null;
        pushClusters(map, overlayRef.current, clusterIndexRef.current);
    }, [data, enabled, map]);

    const getHitsAtPoint = (clientX: number, clientY: number): { items: TripItem[]; hoveredID: string | null } => {
        if (!map) return { items: [], hoveredID: null };
        const mapDiv = map.getDiv() as HTMLElement;
        const rect = mapDiv.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        let closest: { pt: RenderedPoint; dist: number } | null = null;
        for (const pt of renderedPointsRef.current) {
            const dist = Math.hypot(pt.x - x, pt.y - y);
            if (dist <= CLICK_RADIUS && (!closest || dist < closest.dist)) {
                closest = { pt, dist };
            }
        }

        if (!closest) return { items: [], hoveredID: null };

        if (closest.pt.isCluster && closest.pt.clusterID !== undefined && clusterIndexRef.current) {
            const leaves = clusterIndexRef.current.getLeaves(closest.pt.clusterID, Infinity);
            return { items: leaves.map((l) => l.properties.item), hoveredID: `cluster_${closest.pt.clusterID}` };
        }

        if (closest.pt.item) {
            const key = `${closest.pt.item.tripID}:${closest.pt.item.type}:${closest.pt.item.subType}:${closest.pt.item.startTime}`;
            return { items: [closest.pt.item], hoveredID: key };
        }
        return { items: [], hoveredID: null };
    };

    const setHoveredIDs = (ids: Set<string>) => {
        overlayRef.current?.setHoveredIDs(ids);
    };

    const openPopupForItems = (items: TripItem[], position: { x: number; y: number }) => {
        const first = items[0];
        const selectedItemKey = first
            ? `${first.tripID}:${first.type}:${first.subType}:${first.startTime}`
            : "";
        setPopup({
            visible: true,
            position,
            selectedItemKey,
            items: items.map((i) => ({
                tripID:    i.tripID,
                userID:    i.userID,
                type:      i.type,
                subType:   i.subType,
                startTime: i.startTime,
                endTime:   i.endTime,
                formatted: i.formatted,
            })),
        });
    };

    const tripCount = enabled ? (data?.length ?? 0) : 0;

    return { popup, setPopup, getHitsAtPoint, setHoveredIDs, openPopupForItems, tripCount };
}

// --- Helper: push clustered draw points to overlay ---

function pushClusters(
    map: google.maps.Map | null,
    overlay: TripItemsOverlay | null,
    index: Supercluster<TripClusterProps> | null,
) {
    if (!overlay) return;
    if (!map || !index) {
        overlay.setDrawPoints([]);
        return;
    }

    const zoom   = Math.floor(map.getZoom() ?? 0);
    const bounds = map.getBounds();
    if (!bounds) return;

    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const bbox: [number, number, number, number] = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];

    const clusters = index.getClusters(bbox, zoom);

    const drawPoints = clusters.map((c) => {
        const [lng, lat] = c.geometry.coordinates;
        const props = c.properties as any;
        if (props.cluster) {
            return { lng, lat, isCluster: true, clusterID: props.cluster_id as number, count: props.point_count as number };
        }
        return { lng, lat, isCluster: false, item: (props as TripClusterProps).item };
    });

    overlay.setDrawPoints(drawPoints);
}
